import { defineStore } from 'pinia';
import { useLayerStore } from './use-layer-store';
import { EventType, emitter } from '@/utils/emitter.utils';
import { Shape } from '@/services/shape';

export const useEditorStore = defineStore('editor-store', {
  state: () => {
    return {
      // 选中的元素名称
      selectedIds: [] as string[],

      themeId: '',

      // 选择的配色
      themeColorId: '',

      activeShapes: [] as (Shape | undefined)[],

      watchfaceCanvas: {
        scale: 1,
        offsetX: -1,
        offsetY: -1
      },
      // hovered元素
      hovered: '',

      /** not-image：禁用除了图片属性；all：禁用任何属性； false：不禁用 */
      attrDisabled: false as false | 'not-image' | 'all',

      //当前选中的图片
      sku: '',

      /** 画布背景色 */
      canvasBgColor: '#282828FF',

      /** 需要打开数据源的图层 */
      autoOpenDataSourceShapeId: '-',
      imageArrayGuildeModleShown: false,

      // 弧形文本辅助线
      arcAuxiliary: {
        shapeId: '',
        type: 'none'
      },

      // 图层树中展开的keys
      layerTreeExpandkeys: [] as string[],

      // 图层滚动距离顶部的距离
      layersScrollTop: 0,

      // 文字表盘上次输入的内容
      textWatchfaceLastInput: [] as string[],

      iftttPlaying: false
    };
  },

  getters: {
    activeShape(state) {
      return state.activeShapes[0];
    },

    /**
     * 当前选择的theme
     * @param state
     */
    theme(state) {
      const res: StoreThemeItem | undefined = useLayerStore().themes.find(
        (item) => item.id == state.themeId
      );
      return res;
    },

    /**
     * 当前选择的theme color
     * @param state
     */
    themeColor(state) {
      const res: StoreThemeColorItem | undefined = useLayerStore()
        .themes.find((item) => item.id == state.themeId)
        ?.colorGroupTable?.find((item) => item.id == state.themeColorId);
      return res;
    },
    /**
     * 当前选择的theme scale
     * 目前仅图片图组有效
     * @param state
     */
    themeScale(state) {
      const res = useLayerStore()
        .themes.find((item) => item.id == state.themeId)?.scale
      return res || 1;
    },
    /**
     * 当前选择的theme index
     * @param state
     */
    themeIndex(state) {
      const res = useLayerStore()
        .themes.find((item) => item.id == state.themeId)
        ?.colorGroupTable?.findIndex((item) => item.id == state.themeColorId);
      return res;
    },
    /**
     * 当前选择的样式theme在样式列表中的索引（仅 normal 主题）
     * 用于 Rive 动画的 themeID 输入，Rive 设计师期望 0=样式1, 1=样式2, 2=样式3
     * @param state
     */
    themePositionIndex(state) {
      const res = useLayerStore()
        .themes.filter((item) => item.type === 'normal')
        .findIndex((item) => item.id == state.themeId);
      return res;
    }
  },

  actions: {
    /**
     * 选中
     */
    select(selectedIds: string[]) {
      // 全局蒙层不支持多选：多选时排除蒙层，单选蒙层时排除其他元素
      if (selectedIds.length > 1) {
        const maskIds = selectedIds.filter(
          (id) => Shape.getShape(id)?.attrs?._maskFlag
        );
        const normalIds = selectedIds.filter(
          (id) => !Shape.getShape(id)?.attrs?._maskFlag
        );
        // 如果混合选中了蒙层和普通元素，只保留普通元素
        if (maskIds.length > 0 && normalIds.length > 0) {
          selectedIds = normalIds;
        }
      }
      this.selectedIds = [...selectedIds];
      if (!this.selectedIds.includes(this.hovered)) {
        this.hovered = '';
      }
      emitter.emit(EventType['shape:selected']);
    },

    /**
     * 取消选中
     * @param id 要取消的id名称
     */
    unselect(id?: string) {
      this.selectedIds = id ? this.selectedIds.filter((_id) => _id !== id) : [];
      if (!this.selectedIds.includes(this.hovered)) {
        this.hovered = '';
      }
    },

    /**
     * 选择theme
     * @param themeId [可选] 默认选择第一个type的theme
     * @param type [可选] normal AOD
     */
    selectTheme(themeId?: string, type?: StoreThemeItem['type']) {
      if (!themeId) {
        themeId = useLayerStore().themes.filter(
          (item) => item.type == (type ?? 'normal')
        )[0]?.id;
      }

      this.themeId = themeId;
      this.selectThemeColor();
      // 选择主题时
      emitter.emit(EventType['theme:selected']);
    },

    /**
     * 选择theme的colorId
     * @param colorId [可选] 默认选择第一个
     */
    selectThemeColor(colorId?: string) {
      if (
        !colorId ||
        this.theme?.colorGroupTable.every((item) => item.id !== colorId)
      ) {
        colorId = this.theme?.colorGroupTable[0]?.id;
      }
      if (!colorId) return;
      this.themeColorId = colorId;
      emitter.emit(EventType['theme-color:selected']);
    },

    updateLayerTreeExpandkeys(key: string, mode: 'collapse' | 'expand') {
      let keys = [...this.layerTreeExpandkeys];
      if (mode == 'expand') {
        keys.push(key);
      } else {
        keys = keys.filter((key0) => key0 !== key);
      }
      const keys3 = [...new Set(keys)];
      this.layerTreeExpandkeys = [...keys3];
    },

    setLayersScrollTop(scrollTop: number) {
      this.layersScrollTop = scrollTop;
    }
  }
});
